import { useState, useRef, useEffect, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { Bell, LogOut, Search, ChevronDown, Shield } from 'lucide-react'
import voz360Logo from '../../assets/voz360.png'
import { useAuthStore } from '../../store/authStore.js'
import { logout } from '../../auth/logout.js'

export default function PlatformHeader(): JSX.Element {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const initials = user?.email ? user.email.charAt(0).toUpperCase() : '?'

  return (
    <header className="bg-white border-b border-gray-100 h-[60px] flex items-center justify-between px-6 relative z-50 shrink-0">
      {/* Left — Logo + Search */}
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2.5">
          <img src={voz360Logo} alt="Voz360" className="h-8 w-auto object-contain" />
          <span className="text-sm font-semibold text-gray-900 tracking-tight">Platform</span>
        </div>

        {/* Search */}
        <div className="hidden md:flex items-center">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('open-search'))}
            className="flex items-center gap-2 h-9 px-3 rounded-lg bg-gray-50 border border-gray-100 text-sm text-gray-400 hover:bg-gray-100 hover:text-gray-600 hover:border-gray-200 transition-all"
          >
            <Search className="h-4 w-4" />
            <span className="text-[13px]">Search</span>
            <span className="text-[11px] text-gray-300 ml-1 px-1.5 py-0.5 bg-white rounded border border-gray-100">⌘K</span>
          </button>
        </div>
      </div>

      {/* Right — Notifications + Profile */}
      <div className="flex items-center gap-1">
        {/* Notification */}
        <button className="relative h-9 w-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all">
          <Bell className="h-[18px] w-[18px]" />
          <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[#f6931c] ring-2 ring-white" />
        </button>

        {/* Divider */}
        <div className="h-6 w-px bg-gray-100 mx-2" />

        {/* Profile dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2.5 h-9 pl-1.5 pr-2 rounded-lg hover:bg-gray-50 transition-all"
          >
            {/* Avatar */}
            <div className="h-7 w-7 rounded-full bg-[#3454cf] flex items-center justify-center text-white text-xs font-semibold">
              {initials}
            </div>
            <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown */}
          {menuOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-100 overflow-hidden z-50 py-1">
              {/* User info */}
              <div className="px-4 py-3">
                <p className="text-sm font-semibold text-gray-900">{user?.email || 'User'}</p>
                <p className="text-xs text-gray-400 mt-0.5">Platform Administrator</p>
              </div>

              {/* Divider */}
              <div className="h-px bg-gray-50 mx-3" />

              {/* MFA */}
              <div className="p-1.5">
                <button
                  onClick={() => { setMenuOpen(false); navigate('/platform/mfa') }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors"
                >
                  <Shield className="h-4 w-4" />
                  <span>MFA</span>
                </button>
              </div>

              {/* Logout */}
              <div className="p-1.5">
                <button
                  onClick={() => { setMenuOpen(false); void logout().then(() => navigate('/login/platform')) }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
